import React, { useState } from 'react'
import { useParams } from 'react-router'
import { useAuthStore } from '../stores/auth-store'
import { ExternalDataIntegration } from '../components/integrations/ExternalDataIntegration'
import { BatchOperations } from '../components/integrations/BatchOperations'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { PlusIcon, DatabaseIcon, RefreshCwIcon, SettingsIcon } from 'lucide-react'

export function IntegrationsPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const { user } = useAuthStore()
  
  if (!workspaceId) {
    return <div>Invalid workspace</div>
  }
  const [activeTab, setActiveTab] = useState('overview')

  // Mock data for demo - replace with actual data
  const [mockRecords] = useState([
    {
      id: '1',
      title: 'Custom Photo Print Order #12345',
      status: 'pending' as const,
      source: 'Jetsy API',
      priority: 'high' as const,
      issues: ['Missing due date', 'Large image file'],
      preview: {
        taskTitle: 'Photo Print: Wedding Album',
        description: 'Custom wedding album with personalized cover',
        dueDate: '2024-01-15',
        tags: ['urgent', 'wedding', 'custom']
      }
    },
    {
      id: '2', 
      title: 'Business Card Order #67890',
      status: 'success' as const,
      source: 'Shopify Webhook',
      priority: 'medium' as const,
      issues: [],
      preview: {
        taskTitle: 'Business Cards - Tech Startup',
        description: '500 premium business cards with logo',
        dueDate: '2024-01-20',
        tags: ['business', 'printing']
      }
    },
    {
      id: '3',
      title: 'Promotional Banner #54321',
      status: 'error' as const,
      source: 'Custom API',
      priority: 'low' as const,
      issues: ['Invalid dimensions', 'Color format error'],
      errorMessage: 'Image resolution too low for print quality',
      preview: {
        taskTitle: 'Banner: Summer Sale',
        description: 'Large format promotional banner',
        tags: ['marketing', 'banner']
      }
    }
  ])

  const handleBatchAction = async (action: string, recordIds: string[]) => {
    console.log(`Performing ${action} on records:`, recordIds)
    // Simulate async operation
    await new Promise(resolve => setTimeout(resolve, 1000))
  }

  const handleRecordUpdate = (recordId: string, updates: any) => {
    console.log(`Updating record ${recordId}:`, updates)
  }

  const integrationTypes = [
    {
      id: 'jetsy',
      name: 'Jetsy (Etsy Integration)',
      description: 'Import orders directly from your Etsy shop',
      icon: <DatabaseIcon className="w-8 h-8" />,
      status: 'connected',
      lastSync: '2 hours ago',
      recordCount: 156
    },
    {
      id: 'shopify',
      name: 'Shopify',
      description: 'Sync orders and inventory from Shopify',
      icon: <DatabaseIcon className="w-8 h-8" />,
      status: 'disconnected',
      lastSync: 'Never',
      recordCount: 0
    },
    {
      id: 'custom',
      name: 'Custom API',
      description: 'Connect to any REST API endpoint',
      icon: <SettingsIcon className="w-8 h-8" />,
      status: 'configured',
      lastSync: '1 day ago',
      recordCount: 23
    }
  ]

  const getStatusBadge = (status: string) => {
    const variants = {
      connected: { variant: 'default' as const, text: 'Connected', color: 'text-green-600' },
      disconnected: { variant: 'secondary' as const, text: 'Disconnected', color: 'text-gray-500' },
      configured: { variant: 'outline' as const, text: 'Configured', color: 'text-blue-600' }
    }
    const config = variants[status as keyof typeof variants] || variants.disconnected
    
    return (
      <Badge variant={config.variant} className={config.color}>
        {config.text}
      </Badge>
    )
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-krushr-gray-dark">Integrations</h1>
          <p className="text-krushr-gray mt-1">
            Connect external data sources and manage imports
          </p>
        </div>
        <Button>
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Integration
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="setup">Setup New</TabsTrigger>
          <TabsTrigger value="batch">Batch Operations</TabsTrigger>
          <TabsTrigger value="history">Import History</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {integrationTypes.map((integration) => (
              <Card key={integration.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-krushr-gray-bg rounded-lg">
                        {integration.icon}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{integration.name}</CardTitle>
                        <CardDescription className="text-sm">
                          {integration.description}
                        </CardDescription>
                      </div>
                    </div>
                    {getStatusBadge(integration.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-krushr-gray">Last Sync:</span>
                    <span className="font-medium">{integration.lastSync}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-krushr-gray">Records:</span>
                    <span className="font-medium">{integration.recordCount}</span>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" variant="outline" className="flex-1">
                      <SettingsIcon className="w-3 h-3 mr-1" />
                      Configure
                    </Button>
                    <Button 
                      size="sm" 
                      className="flex-1"
                      disabled={integration.status === 'disconnected'}
                    >
                      <RefreshCwIcon className="w-3 h-3 mr-1" />
                      Sync Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest integration events and sync results</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div>
                      <div className="font-medium">Jetsy sync completed</div>
                      <div className="text-sm text-gray-500">Imported 12 new orders, updated 3 existing</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">2 hours ago</div>
                </div>
                
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <div>
                      <div className="font-medium">Custom API configured</div>
                      <div className="text-sm text-gray-500">New endpoint added with authentication</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">1 day ago</div>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <div>
                      <div className="font-medium">Shopify connection failed</div>
                      <div className="text-sm text-gray-500">Authentication token expired</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">3 days ago</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="setup">
          <ExternalDataIntegration workspaceId={workspaceId} />
        </TabsContent>

        <TabsContent value="batch">
          <BatchOperations
            records={mockRecords}
            onBatchAction={handleBatchAction}
            onRecordUpdate={handleRecordUpdate}
          />
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Import History</CardTitle>
              <CardDescription>View and manage previous data imports</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                <DatabaseIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Import history will appear here after you run your first data sync</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default IntegrationsPage